import { useCallback, useRef, useEffect } from 'react';
import { GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';

/**
 * Options for grid selection handler
 */
export interface UseGridSelectionHandlerOptions<T> {
  /**
   * Function to extract row ID from row data
   */
  getRowId: (row: T) => string;

  /**
   * Optional function to expand group IDs to their member row IDs
   * Used for hierarchical/grouped data grids (e.g., Portfolio page)
   */
  expandGroups?: (groupId: string, visibleRowIds: Set<string>) => string[];
}

/**
 * Custom hook that creates a selection handler for MUI DataGrid Premium.
 *
 * This hook solves the problem of selections being cleared when the displayed data changes
 * (e.g., during search/filter operations). It implements delta-based selection merging:
 *
 * 1. Tracks which rows are currently visible
 * 2. Computes what changed (added vs removed) from the visible set only
 * 3. Applies the delta to preserve selections of non-visible rows
 *
 * @example
 * ```typescript
 * const handleRowSelectionChange = useGridSelectionHandler({
 *   selectedRows,
 *   setSelectedRows,
 *   displayData,
 *   options: {
 *     getRowId: (row) => row.id,
 *   }
 * });
 * ```
 *
 * @param selectedRows - Current array of selected row IDs
 * @param setSelectedRows - State setter for selected rows
 * @param displayData - Currently displayed/filtered data
 * @param options - Configuration options
 * @returns Selection change handler for DataGrid's onRowSelectionModelChange
 */
export function useGridSelectionHandler<T>(
  selectedRows: string[],
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>,
  displayData: T[],
  options: UseGridSelectionHandlerOptions<T>
): (newSelection: GridRowSelectionModel) => void {
  const { getRowId, expandGroups } = options;

  // Ref to track latest displayData to avoid stale closures
  const displayDataRef = useRef<T[]>(displayData);

  // Update ref when displayData changes
  useEffect(() => {
    displayDataRef.current = displayData;
  }, [displayData]);

  return useCallback(
    (newSelection: GridRowSelectionModel) => {
      try {
        // Validate selection model format
        if (
          !newSelection ||
          typeof newSelection !== 'object' ||
          !('ids' in newSelection)
        ) {
          setSelectedRows([]);
          return;
        }

        const { ids, type } = newSelection;

        if (!(ids instanceof Set)) {
          setSelectedRows([]);
          return;
        }

        // Get currently visible row IDs using ref for latest displayData
        const visibleRowIds = new Set(
          displayDataRef.current.map(row => getRowId(row))
        );

        if (type === 'include') {
          // Include mode: Select specific rows
          setSelectedRows(prev => {
            // Get currently visible selections (what DataGrid knows about)
            const currentVisibleSelections = prev.filter(id =>
              visibleRowIds.has(id)
            );
            const currentVisibleSet = new Set(currentVisibleSelections);

            // Get new visible selections from DataGrid
            let newVisibleSelections: string[] = [];

            // Process each selected ID
            Array.from(ids).forEach(id => {
              const idString = id.toString();

              // Check if this is a group row (for pages with grouping)
              if (expandGroups && idString.startsWith('group-')) {
                // Expand group to its member row IDs
                const groupMembers = expandGroups(idString, visibleRowIds);
                newVisibleSelections.push(...groupMembers);
              } else {
                // Regular row - add if visible
                if (visibleRowIds.has(idString)) {
                  newVisibleSelections.push(idString);
                }
              }
            });

            // Remove duplicates
            newVisibleSelections = [...new Set(newVisibleSelections)];
            const newVisibleSet = new Set(newVisibleSelections);

            // Compute delta (added and removed) - optimized with Sets
            const added = newVisibleSelections.filter(
              id => !currentVisibleSet.has(id)
            );
            const removed = currentVisibleSelections.filter(
              id => !newVisibleSet.has(id)
            );

            // Apply delta: remove 'removed' IDs and add 'added' IDs
            const removedSet = new Set(removed);
            const updated = prev.filter(id => !removedSet.has(id));
            return [...updated, ...added];
          });
        } else if (type === 'exclude') {
          // Exclude mode: Select all except excluded rows
          setSelectedRows(prev => {
            // Get currently visible selections
            const currentVisibleSelections = prev.filter(id =>
              visibleRowIds.has(id)
            );
            const currentVisibleSet = new Set(currentVisibleSelections);

            // Get all visible row IDs
            const allVisibleIds = displayDataRef.current.map(row =>
              getRowId(row)
            );

            // Calculate excluded IDs (handle groups if applicable)
            const excludedIds = Array.from(ids).map(id => id.toString());
            const finalExcludedIds: string[] = [...excludedIds];

            if (expandGroups) {
              excludedIds.forEach(id => {
                if (id.startsWith('group-')) {
                  // Expand excluded group to its members
                  const groupMembers = expandGroups(id, visibleRowIds);
                  finalExcludedIds.push(...groupMembers);
                }
              });
            }

            const excludedSet = new Set(finalExcludedIds);

            // New visible selections = all visible except excluded
            const newVisibleSelections = allVisibleIds.filter(
              id => !excludedSet.has(id)
            );
            const newVisibleSet = new Set(newVisibleSelections);

            // Compute delta
            const added = newVisibleSelections.filter(
              id => !currentVisibleSet.has(id)
            );
            const removed = currentVisibleSelections.filter(
              id => !newVisibleSet.has(id)
            );

            // Apply delta
            const removedSet = new Set(removed);
            const updated = prev.filter(id => !removedSet.has(id));
            return [...updated, ...added];
          });
        } else {
          // Unknown type - clear selection
          setSelectedRows([]);
        }
      } catch (error) {
        // Error handling: log and clear selection to prevent UI issues
        console.error('Grid selection handler error:', error);
        showSnackbar({
          title: 'Selection Error',
          message: 'Failed to update selection. Please try again.',
          type: SnackbarType.error,
        });
        setSelectedRows([]);
      }
    },
    [getRowId, expandGroups, setSelectedRows]
  );
}
